import React, { ChangeEvent, Dispatch, SetStateAction, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Tooltip, // Add Tooltip import
} from '@mui/material';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'; // Add InfoOutlinedIcon import
import TablePaginationActions from '@mui/material/TablePagination/TablePaginationActions';
import { ColumnConfigType } from 'shared/types';
import { LoadingButton } from '@mui/lab';
import { StyledTableContent, StyledTableRow } from './StyledTableContent';

type PaginationType = {
  rowsPerPage: number;
  page: number;
};

type DataTableBackendPaginationType<DataType> = {
  title: string;
  search?: boolean;
  searchPlaceholder?: string;
  onSearch?: (search: string) => void;
  data: DataType[];
  total: number;
  pagination: PaginationType;
  setPagination: Dispatch<SetStateAction<{ rowsPerPage: number; page: number }>>;
  columnConfig: ColumnConfigType<DataType>[];
  deleteIcon?: boolean;
  handleDrawerDelete?: (id: number, variant: string, title: string) => void;
  variantDrawer?: string;
  titleDrawer?: string;
  emptyListTitle?: string;
  onRowClick?: (id: number | string) => void;
  buttonsController?: boolean;
  isLoadingDownload?: boolean;
  onUpdateTable?: () => void;
  onDownloadTable?: () => void;
  onDownloadRow?: (e: any) => void;
};

export const DataTableBackendPagination = <DataType,>({
  title,
  search,
  searchPlaceholder = 'Поиск',
  onSearch,
  data,
  total,
  pagination,
  setPagination,
  columnConfig,
  deleteIcon,
  handleDrawerDelete,
  onRowClick,
  variantDrawer = '',
  titleDrawer = '',
  emptyListTitle = 'Нет данных для отображения',
  buttonsController,
  isLoadingDownload = false,
  onUpdateTable,
  onDownloadTable,
  onDownloadRow,
}: DataTableBackendPaginationType<DataType>) => {
  const [searchText, setSearchText] = useState('');
  const [showSearch, setShowSearch] = useState(false);

  const handleChangePageParticipants = (event: any, newPage: number) => {
    setPagination({ ...pagination, page: newPage });
  };

  const handleChangeSearch = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    if (value.includes('+')) {
      const sanitizedValue = value.replace('+', '');
      setSearchText(sanitizedValue);
      onSearch?.(sanitizedValue);
    } else {
      setSearchText(value);
      onSearch?.(value);
    }
  };

  const closeSearchButton = () => {
    onSearch?.('');
    setSearchText('');
    setShowSearch(false);
  };

  const handleChangeRowsPerPageParticipants = (event: any) =>
    setPagination({
      ...pagination,
      rowsPerPage: parseInt(event.target.value, 10),
      page: 0,
    });

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {search ? (
          <Box>
            {showSearch ? (
              <Box>
                <SearchOutlinedIcon />
                <TextField
                  autoFocus
                  variant="standard"
                  value={searchText}
                  size="small"
                  sx={{ width: '425px', pl: '5px', pr: '10px' }}
                  onChange={handleChangeSearch}
                  placeholder={searchPlaceholder}
                />
                <IconButton onClick={closeSearchButton}>
                  <CloseOutlinedIcon />
                </IconButton>
              </Box>
            ) : (
              <Box
                display="flex"
                alignItems="center"
              >
                <IconButton onClick={() => setShowSearch(true)}>
                  <SearchOutlinedIcon />
                </IconButton>
                <Typography marginLeft="10px">{title}</Typography>
              </Box>
            )}
          </Box>
        ) : (
          <Typography>{title}</Typography>
        )}
        {buttonsController && (
          <div style={{ marginLeft: '30px' }}>
            {onDownloadTable && (
              <LoadingButton
                loading={isLoadingDownload}
                disabled={isLoadingDownload}
                onClick={onDownloadTable}
                variant="outlined"
              >
                Скачать
              </LoadingButton>
            )}
            <Button
              onClick={onUpdateTable}
              style={{ marginLeft: '10px' }}
              variant="outlined"
            >
              Обновить
            </Button>
          </div>
        )}
      </div>
      {data && data.length > 0 ? (
        <StyledTableContent>
          <Box
            style={{
              marginTop: '12px',
            }}
          >
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {columnConfig.map((column, index) => (
                      <TableCell
                        key={index}
                        style={{ width: column.width }}
                      >
                        {column.name}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.map((data: any, idx: number) => (
                    <StyledTableRow
                      hover
                      onClick={() => onRowClick && onRowClick(data.id)}
                      key={idx}
                    >
                      {columnConfig.map((column, idx) => (
                        <TableCell
                          key={idx}
                          style={{ width: column.width }}
                        >
                          {column.dataFieldName === 'plan' && data[column.dataFieldName] === -1 ? (
                            <Tooltip title="План акции можно посмотреть на странице с деталями соответствующей механики">
                              <InfoOutlinedIcon
                                fontSize="small"
                                sx={{ verticalAlign: 'middle' }}
                              />
                            </Tooltip>
                          ) : (
                            data[column.dataFieldName]
                          )}
                        </TableCell>
                      ))}
                      {deleteIcon && (
                        <TableCell>
                          <IconButton
                            onClick={(event) => {
                              event.stopPropagation();
                              handleDrawerDelete?.(data.id, variantDrawer, titleDrawer);
                            }}
                          >
                            <CloseOutlinedIcon />
                          </IconButton>
                        </TableCell>
                      )}
                      {onDownloadRow && data.isShowDownloadButton && (
                        <TableCell>
                          <Button
                            onClick={(e) => {
                              e?.stopPropagation();
                              onDownloadRow(idx);
                            }}
                            style={{ width: '135px' }}
                          >
                            Скачать excel
                          </Button>
                        </TableCell>
                      )}
                    </StyledTableRow>
                  ))}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TablePagination
                      rowsPerPageOptions={[5, 10, 25]}
                      count={total}
                      rowsPerPage={pagination.rowsPerPage}
                      labelRowsPerPage="Строк на странице"
                      page={pagination.page}
                      onPageChange={handleChangePageParticipants}
                      onRowsPerPageChange={handleChangeRowsPerPageParticipants}
                      ActionsComponent={TablePaginationActions}
                    />
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          </Box>
        </StyledTableContent>
      ) : (
        <Box marginTop="30px">
          <Typography>{emptyListTitle}</Typography>
        </Box>
      )}
    </div>
  );
};
